/**
 * Jump-anywhere chat history.
 *
 * A topic's chat log is a single contiguous window of history. Normally it
 * ends at the latest message and grows backwards as the user scrolls up. A
 * jump (quote, search hit, deeplink) replaces it with a window fetched around
 * the target, so the window can end before the latest message: it is then
 * "detached" (`hasNewer`) and live messages are counted instead of appended,
 * because appending them would leave a silent gap in the middle of the log.
 *
 * Windows are capped at HISTORY_WINDOW_MAX messages; growing one end trims the
 * other, which keeps long scroll sessions light. Trimming the newest end
 * detaches the window; trimming the oldest end re-arms loading older messages.
 */

export const HISTORY_WINDOW_MAX = 300;
export const JUMP_WINDOW_SIZE = 60;

/** chatLogs are keyed by topic, or held at the conversation for DMs. */
export const ROOT_WINDOW_KEY = "_root";
export const windowKey = (topicId) => topicId || ROOT_WINDOW_KEY;

/**
 * Log generations. Replacing a log (a jump, or loading the latest page)
 * starts a new generation; a page requested under an older one is discarded
 * when it lands. Keyset pages are additionally checked against the log's
 * edge (see conversationHistory), which also catches a page requested after
 * the replace started but from the old window.
 */
const generations = new Map();
const generationKey = (conversationId, topicId) =>
  `${conversationId}:${windowKey(topicId)}`;
export const logGeneration = (conversationId, topicId) =>
  generations.get(generationKey(conversationId, topicId)) || 0;
export const bumpLogGeneration = (conversationId, topicId) => {
  const next = logGeneration(conversationId, topicId) + 1;
  generations.set(generationKey(conversationId, topicId), next);
  return next;
};

export const selectHistoryWindow = (state, conversationId, topicId) =>
  state.chat?.historyWindows?.[conversationId]?.[windowKey(topicId)];

/** A conversation.history document as the chat log stores it. */
export const toChatMessage = (messageDoc) => ({
  ...messageDoc.content,
  messageId: messageDoc._id,
  rootCreatedAt: messageDoc.createdAt,
  metadata: {
    ...messageDoc.metadata,
    ...messageDoc.content?.metadata,
  },
});

/** Keyset cursors need a stored message id, not a temp/optimistic one. */
export const isServerMessageId = (messageId) =>
  typeof messageId === "string" && /^[0-9a-fA-F]{24}$/.test(messageId);

/**
 * Cap a window after one end grew. `grewOlder` trims the newest end (the
 * user is reading back), otherwise the oldest end.
 * @returns {{ messages: object[], trimmedNewer: boolean, trimmedOlder: boolean }}
 */
export const trimWindow = (messages, grewOlder, max = HISTORY_WINDOW_MAX) => {
  if (!messages || messages.length <= max) {
    return { messages, trimmedNewer: false, trimmedOlder: false };
  }
  return grewOlder
    ? { messages: messages.slice(0, max), trimmedNewer: true, trimmedOlder: false }
    : { messages: messages.slice(-max), trimmedNewer: false, trimmedOlder: true };
};
